import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { EntityTag } from "#app/components/entity";
import {
  ALLOWED_ATTR,
  ALLOWED_TAGS,
  ATTR_TAG_SCOPE,
  sanitizeStyleAttribute,
} from "./html-allow-list";
import {
  fallbackMentionEntity,
  isMentionElement,
  issueOpenConfirm,
  parseMentionElement,
  type EntityMentionRef,
} from "./mention-refs";
import { useMentionEntityResolver } from "./use-mention-entity-resolver";
import { useResolvedMediaHtml } from "#app/lib/hooks/use-note-media";
import {
  TASK_ITEM_SELECTOR,
  taskItemCheckbox,
  taskItemLabel,
} from "./task-items";
import "./editor-styles.css";
import type { Omittable } from "@salestrak/ui";

type SanitizedHtmlProps = Omittable<{
  html: string;
  /** Fired with the img element's current src when an inline image is
   * clicked (delegated listener — the HTML subtree is React-unmanaged). */
  onImageClick?: (src: string) => void;
  /** Inline images render as thumbnail tiles from their thumb variant. */
  mediaThumbnails?: boolean;
  /** Saves a checklist tick: the item's index in document order and its new
   *  state; resolve `false` to put the box back. Without it the boxes are
   *  read-only — a tick nothing saves would only look saved. */
  onTaskToggle?: (index: number, checked: boolean) => Promise<boolean>;
}>;

function extractMentionSlots(sanitized: string): {
  html: string;
  mentions: EntityMentionRef[];
} {
  if (
    (!sanitized.includes("data-id") && !sanitized.includes("data-type")) ||
    typeof document === "undefined"
  ) {
    return { html: sanitized, mentions: [] };
  }
  const root = document.createElement("div");
  root.innerHTML = sanitized;
  const mentions: EntityMentionRef[] = [];
  for (const el of Array.from(
    root.querySelectorAll<HTMLElement>("span[data-id], span[data-type]"),
  )) {
    if (!isMentionElement(el)) continue;
    mentions.push(parseMentionElement(el));
    el.setAttribute("data-mention-slot", String(mentions.length - 1));
    el.removeAttribute("data-id");
    el.removeAttribute("data-type");
    el.removeAttribute("data-label");
    el.removeAttribute("data-workflow-id");
    el.className = "entity-chip";
    el.textContent = "";
  }
  return { html: root.innerHTML, mentions };
}

function MentionChip({ mention }: { mention: EntityMentionRef }) {
  const resolve = useMentionEntityResolver();
  const entity = resolve(mention) ?? fallbackMentionEntity(mention);
  // A chip persisted without an id has nothing to navigate to — render display-only.
  const hasTarget = !!mention.id;
  return (
    <EntityTag
      entity={entity}
      variant="chip"
      interactive={hasTarget}
      confirmOnClick={hasTarget ? issueOpenConfirm(entity) : undefined}
    />
  );
}

export function SanitizedHtml({
  html,
  onImageClick,
  mediaThumbnails = false,
  onTaskToggle,
}: SanitizedHtmlProps) {
  const { html: sanitized, mentions } = useMemo(() => {
    // DOMPurify hooks are GLOBAL: install for this sanitize only and remove in
    // `finally`, or it would affect every other DOMPurify consumer in the bundle.
    DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
      if (data.attrName === "style") {
        const safe = sanitizeStyleAttribute(data.attrValue ?? "");
        if (safe) data.attrValue = safe;
        else data.keepAttr = false;
        return;
      }
      const allowedTags = ATTR_TAG_SCOPE[data.attrName];
      if (allowedTags && !allowedTags.includes(node.tagName)) {
        data.keepAttr = false;
      }
    });
    try {
      const clean = DOMPurify.sanitize(html ?? "", {
        ALLOWED_TAGS: [...ALLOWED_TAGS],
        ALLOWED_ATTR: [...ALLOWED_ATTR],
      });
      return extractMentionSlots(clean);
    } finally {
      DOMPurify.removeHook("uponSanitizeAttribute");
    }
  }, [html]);

  // Photo srcs resolve AFTER sanitization: they become blob: object URLs,
  // which DOMPurify's URI policy strips. Safe to inject unsanitized — the
  // resolver only swaps srcs matching the strict /media/:id/content pattern
  // for object URLs it created itself.
  const { html: resolved } = useResolvedMediaHtml(
    sanitized,
    mediaThumbnails ? "thumb" : "full",
  );

  // HTML is injected via `innerHTML` (not `dangerouslySetInnerHTML`) so React
  // never manages this subtree — otherwise its second commit clobbers the chip
  // portals mounted into the slot hosts inside the string.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hosts, setHosts] = useState<(HTMLElement | undefined)[]>([]);
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    root.innerHTML = resolved;
    if (mentions.length === 0) {
      setHosts([]);
      return;
    }
    const found: (HTMLElement | undefined)[] = new Array(mentions.length);
    for (const el of Array.from(
      root.querySelectorAll<HTMLElement>("[data-mention-slot]"),
    )) {
      found[Number(el.getAttribute("data-mention-slot"))] = el;
    }
    setHosts(found);
  }, [resolved, mentions]);

  // Checklist boxes: named by their item's text, and live only when a tick
  // can be saved. Runs after the injection above (effects run in order).
  const onTaskToggleRef = useRef(onTaskToggle);
  onTaskToggleRef.current = onTaskToggle;
  const tasksEditable = !!onTaskToggle;
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll(TASK_ITEM_SELECTOR));
    for (const item of items) {
      const box = taskItemCheckbox(item);
      if (!box) continue;
      box.checked = item.getAttribute("data-checked") === "true";
      box.disabled = !tasksEditable;
      box.setAttribute("aria-label", taskItemLabel(item) || "Checklist item");
    }
    if (!tasksEditable) return;
    const onChange = (event: Event) => {
      const box = event.target;
      if (!(box instanceof HTMLInputElement) || box.type !== "checkbox") return;
      const item = box.closest(TASK_ITEM_SELECTOR);
      const index = item ? items.indexOf(item) : -1;
      if (!item || index < 0 || taskItemCheckbox(item) !== box) return;
      const checked = box.checked;
      item.setAttribute("data-checked", String(checked));
      void onTaskToggleRef.current?.(index, checked).then((saved) => {
        if (saved) return;
        box.checked = !checked;
        item.setAttribute("data-checked", String(!checked));
      });
    };
    root.addEventListener("change", onChange);
    return () => root.removeEventListener("change", onChange);
  }, [resolved, mentions, tasksEditable]);

  // Image clicks (fullscreen viewer): the injected subtree is outside
  // React, so delegate from the container.
  const onImageClickRef = useRef(onImageClick);
  onImageClickRef.current = onImageClick;
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root || !onImageClick) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof HTMLImageElement && target.src) {
        onImageClickRef.current?.(target.src);
      }
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [onImageClick]);

  // Editor-module DOM exception: no @salestrak/ui primitive can inject a
  // pre-sanitized HTML string; createElement keeps the raw div explicit.
  return (
    <>
      {React.createElement("div", {
        ref: containerRef,
        className: `rich-content note-content${onImageClick ? " photo-clickable" : ""}${mediaThumbnails ? " media-thumbs" : ""}`,
      })}
      {hosts.map((host, index) => {
        const mention = mentions[index];
        if (!host || !mention) return null;
        return createPortal(
          <MentionChip mention={mention} />,
          host,
          `${mention.kind}-${mention.id}-${index}`,
        );
      })}
    </>
  );
}
